import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Share, PlusSquare, Smartphone } from "lucide-react";

/** Detect platform / browser for tailored install behavior. */
function detectEnv() {
  if (typeof navigator === "undefined") return { platform: "unknown", browser: "unknown" };
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const platform = isIOS ? "ios" : isAndroid ? "android" : "desktop";
  return { platform, isIOS, isAndroid };
}

/** Always-visible "Download AAMG app" button.
 *  • Native install prompt when available (Android Chrome / desktop Chromium) → one-tap install
 *  • iOS Safari: shows the Share → Add to Home Screen guide (Apple gives us no programmatic option)
 *  • Auto-hides once installed (display-mode: standalone)
 */
export default function InstallPWAButton({ variant = "default", className = "", label = "Download app", size = "default" }) {
  const [deferred, setDeferred] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const env = detectEnv();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // AUTO-FIRE on Android the moment Chrome makes it available — no user click needed.
      // Chrome only fires beforeinstallprompt after engagement criteria, so by the time we
      // get here, the user has already shown intent.
      if (env.isAndroid && !sessionStorage.getItem("aamg_install_auto_fired")) {
        sessionStorage.setItem("aamg_install_auto_fired", "1");
        // Defer slightly so we don't conflict with React render
        setTimeout(() => {
          try {
            e.prompt();
            e.userChoice.then((choice) => {
              setDeferred(null);
              if (choice?.outcome === "accepted") {
                // Auto-request notification permission right after install accept
                if ("Notification" in window && Notification.permission === "default") {
                  Notification.requestPermission().catch(() => {});
                }
              }
            });
          } catch {}
        }, 400);
      }
    };
    const onInstalled = () => {
      setIsStandalone(true);
      setDeferred(null);
      toast.success("AAMG installed — look on your home screen!");
      // Auto-request notification permission so installed users get push immediately
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [env.isAndroid]);

  if (isStandalone) return null;

  const triggerInstall = async () => {
    if (deferred) {
      // Native install — one tap on Android Chrome, Edge, Samsung Internet, or desktop Chromium
      try {
        deferred.prompt();
        await deferred.userChoice;
        setDeferred(null);
      } catch {
        toast.error("Install canceled");
      }
      return;
    }
    if (env.isIOS) {
      // iOS Safari has no install API — show the Share → Add to Home Screen guide
      setShowIOSHint(true);
      return;
    }
    // Android/desktop without a deferred prompt — engagement criteria not met yet.
    // Show a one-line toast (no dialog) telling the user how to install via the browser menu.
    toast.info(
      env.isAndroid
        ? "Tap the ⋮ menu in your browser, then Install app."
        : "Use Chrome or Edge → click the ⊕ icon in the address bar to install.",
      { duration: 6000 }
    );
  };

  return (
    <>
      <Button
        type="button"
        onClick={triggerInstall}
        variant={variant}
        size={size}
        className={`gap-2 ${variant === "default" ? "bg-[#339999] hover:bg-[#2A7F7F] text-white" : ""} ${className}`}
        data-testid="install-pwa-btn"
      >
        <Download className="h-4 w-4" /> {label}
      </Button>

      <Dialog open={showIOSHint} onOpenChange={setShowIOSHint}>
        <DialogContent className="max-w-md" data-testid="install-ios-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-[#339999]" />
              Add AAMG to your Home Screen
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              iPhone requires this short manual step — Apple doesn't allow apps to install themselves.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm text-slate-700 mt-2">
            <li className="flex items-start gap-3">
              <span className="shrink-0 h-6 w-6 rounded-full bg-[#339999] text-white text-xs font-semibold flex items-center justify-center">1</span>
              <div>Make sure you're using <strong>Safari</strong> (not Chrome).</div>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 h-6 w-6 rounded-full bg-[#339999] text-white text-xs font-semibold flex items-center justify-center">2</span>
              <div className="flex items-center gap-2">Tap <Share className="inline h-4 w-4 text-[#339999]" /> <strong>Share</strong> at the bottom.</div>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 h-6 w-6 rounded-full bg-[#339999] text-white text-xs font-semibold flex items-center justify-center">3</span>
              <div className="flex items-center gap-2">Tap <PlusSquare className="inline h-4 w-4 text-[#339999]" /> <strong>Add to Home Screen</strong>.</div>
            </li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
